/**
 * The game's single typeface: Departure Mono, a monospaced pixel font
 * (SIL OFL 1.1 — https://departuremono.com). The DOM overlay picks it up from
 * the CSS `@font-face` in `src/app/style.css`, but the Pixi canvas rasterises
 * text itself and needs the glyphs resident *before* the first {@link Text} is
 * built — otherwise the heading renders in a fallback for a frame or two. We
 * load it explicitly here and await it during renderer boot.
 */
export const GAME_FONT_FAMILY = 'Departure Mono';

const FONT_URL = '/fonts/DepartureMono-Regular.woff2';

let loadPromise: Promise<void> | null = null;

/**
 * Register Departure Mono with the document and resolve once its glyphs are
 * ready to rasterise. Idempotent (the load is memoised) and a no-op in
 * environments without the Font Loading API (e.g. the Node test runner).
 */
export function loadGameFonts(): Promise<void> {
  loadPromise ??= (async () => {
    if (typeof FontFace === 'undefined' || typeof document === 'undefined') {
      return;
    }
    try {
      const face = new FontFace(GAME_FONT_FAMILY, `url(${FONT_URL}) format('woff2')`);
      await face.load();
      document.fonts.add(face);
    } catch {
      // A failed font load must never block the render boot — the platform
      // monospace fallback keeps the UI legible.
    }
  })();
  return loadPromise;
}
